import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getSunatDocLabel } from '../../utils/sunatDocumentTypes';
import { getSunatMappings, saveSunatMappings } from '../../services/sunatMappingService';
import ProductSelector from '../PurchaseOrders/ProductSelector';
import UnitIdSelect from '../Common/UnitIdSelect';
import QuantityConversionHint from '../Common/QuantityConversionHint';
import { useModuleSettings } from '../../contexts/ModuleSettingsContext';
import {
  findUnitIdByCode,
  resolveUnitIdForProduct,
} from '../../utils/unitSelectUtils';
import Toast from '../Common/Toast';

/** Clave estable para mapear línea SUNAT (código proveedor + fila, para no colisionar). */
export function getSunatLineKey(prod, index = 0) {
  const code = String(prod?.internal_code ?? '').trim();
  const lineNum = String(prod?.line_number ?? '').trim() || String(index + 1);
  if (code) return `${code}::${lineNum}`;
  if (lineNum) return `LINE-${lineNum}`;
  const desc = String(prod?.description ?? '').trim();
  if (desc) return `DESC-${desc.slice(0, 80)}::${lineNum}`;
  return `ROW-${index + 1}`;
}

/** Código de proveedor usado al guardar/cargar mapeos (sin sufijo de fila). */
export function getSunatSupplierCode(prod, lineKey = '') {
  const code = String(prod?.internal_code ?? '').trim();
  if (code) return code;
  const key = String(lineKey || '').trim();
  if (key.includes('::')) return key.split('::')[0];
  return key;
}

const SunatSearchResultModal = ({
  isOpen,
  onClose,
  onApply,
  data,
  products = [],
  getProduct = null,
  productSelectorAsyncProps = null,
  onEnsureProducts = null,
  units = [],
  supplierId,
}) => {
  const { productConversionsEnabled } = useModuleSettings();
  const [mappedIds, setMappedIds] = useState({});
  const [initialMappings, setInitialMappings] = useState({});
  const [loadingMappings, setLoadingMappings] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'error' });
  const [confirmClose, setConfirmClose] = useState(false);

  const showToast = useCallback((message, type = 'error') => {
    setToast({ isOpen: true, message, type });
  }, []);

  const sunatProducts = data?.products;

  const sunatLines = useMemo(
    () =>
      (sunatProducts || []).map((p, index) => ({
        ...p,
        lineKey: getSunatLineKey(p, index),
      })),
    [sunatProducts]
  );

  const allMapped = useMemo(
    () =>
      sunatLines.every((p) => {
        const mapping = mappedIds[p.lineKey];
        return mapping && mapping.product_id != null && mapping.unit_id != null;
      }),
    [sunatLines, mappedIds]
  );

  const getMapping = useCallback(
    (lineKey) => mappedIds[lineKey] || { product_id: null, unit_id: null },
    [mappedIds]
  );

  const sunatProductMap = useMemo(
    () => new Map(sunatLines.map((p) => [p.lineKey, p])),
    [sunatLines]
  );

  const resolveProduct = useCallback(
    (productId) => {
      if (!productId) return null;
      const pid = parseInt(productId, 10);
      if (!pid) return null;
      if (typeof getProduct === 'function') {
        return getProduct(pid);
      }
      return products.find((p) => Number(p.id) === pid) || null;
    },
    [getProduct, products],
  );

  const handleProductChange = useCallback(
    (lineKey, productId, sunatLine, pickedProduct = null) => {
      if (!lineKey) return;
      const pid = productId ? parseInt(productId, 10) : null;
      const product = pickedProduct || resolveProduct(pid);
      let unitId = null;

      if (sunatLine?.unit_code && product) {
        unitId = resolveUnitIdForProduct({
          units,
          product,
          rawUnit: sunatLine.unit_code,
          productConversionsEnabled,
        });
      }
      if (!unitId && product?.unit_of_measure) {
        unitId = findUnitIdByCode(product.unit_of_measure, units);
      }
      if (!unitId && sunatLine?.unit_code) {
        unitId = findUnitIdByCode(sunatLine.unit_code, units);
      }
      setMappedIds((prev) => {
        const current = prev[lineKey] || {};
        return {
          ...prev,
          [lineKey]: {
            product_id: pid,
            unit_id: unitId || current.unit_id || null,
          },
        };
      });
    },
    [resolveProduct, units, productConversionsEnabled]
  );

  const handleUnitChange = useCallback((lineKey, unitId) => {
    if (!lineKey) return;
    setMappedIds((prev) => {
      const current = prev[lineKey] || {};
      return {
        ...prev,
        [lineKey]: {
          product_id: current.product_id || null,
          unit_id: unitId ? parseInt(unitId, 10) : null,
        },
      };
    });
  }, []);

  const hasChanges = useMemo(() => {
    const eq = (a, b) => {
      if (a == null && b == null) return true;
      if (a == null || b == null) return false;
      return a.product_id === b.product_id && a.unit_id === b.unit_id;
    };
    return (
      Object.keys(mappedIds).some((key) => !eq(mappedIds[key], initialMappings[key])) ||
      Object.keys(initialMappings).some((key) => !(key in mappedIds))
    );
  }, [mappedIds, initialMappings]);

  const handleClose = useCallback(() => {
    if (sunatLines.length > 0 && !allMapped) {
      setConfirmClose(true);
      return;
    }
    onClose();
  }, [sunatLines, allMapped, onClose]);

  const handleConfirmClose = useCallback(() => {
    setConfirmClose(false);
    onClose();
  }, [onClose]);

  const handleCancelClose = useCallback(() => {
    setConfirmClose(false);
  }, []);

  const onEnsureProductsRef = useRef(onEnsureProducts);
  useEffect(() => {
    onEnsureProductsRef.current = onEnsureProducts;
  }, [onEnsureProducts]);

  const handleSaveMappings = useCallback(async () => {
    if (!supplierId) {
      showToast('Seleccione un proveedor en el documento antes de guardar mapeos.', 'warning');
      return;
    }

    const entries = Object.entries(mappedIds).filter(([, mapping]) => {
      return mapping && mapping.product_id != null && mapping.product_id > 0;
    });
    if (entries.length === 0) {
      showToast('No hay mapeos válidos para guardar. Selecciona productos primero.', 'warning');
      return;
    }

    setSaving(true);
    try {
      const bySupplierCode = new Map();
      entries.forEach(([lineKey, mapping]) => {
        const sp = sunatProductMap.get(lineKey);
        const supplierCode = getSunatSupplierCode(sp, lineKey);
        if (!supplierCode) return;
        bySupplierCode.set(supplierCode, {
          supplier_product_code: supplierCode,
          product_id: mapping.product_id,
          unit_id: mapping.unit_id || null,
          supplier_product_name: sp?.description || null,
          supplier_unit_code: sp?.unit_code || null,
        });
      });
      const mappings = [...bySupplierCode.values()];
      const res = await saveSunatMappings(supplierId, mappings);
      if (res.success && res.data) {
        const savedByCode = {};
        res.data.forEach((m) => {
          savedByCode[m.supplier_product_code] = { product_id: m.product_id, unit_id: m.unit_id };
        });
        const nextMapped = { ...mappedIds };
        sunatLines.forEach((line) => {
          const code = getSunatSupplierCode(line, line.lineKey);
          if (savedByCode[code]) {
            nextMapped[line.lineKey] = savedByCode[code];
          }
        });
        setInitialMappings(nextMapped);
        setMappedIds(nextMapped);
        showToast('Mapeos guardados exitosamente', 'success');
      } else {
        showToast(res.message || 'Error al guardar mapeos', 'error');
      }
    } catch {
      showToast('Error de conexión al guardar mapeos', 'error');
    } finally {
      setSaving(false);
    }
  }, [supplierId, mappedIds, showToast, sunatProductMap, sunatLines]);

  const handleApply = useCallback(() => {
    if (!allMapped) {
      showToast(
        'Hay productos sin mapear. Asigne producto interno y unidad en cada fila antes de aplicar.',
        'warning'
      );
      return;
    }
    const productsWithMapping = sunatLines.map((p) => {
      const mapping = getMapping(p.lineKey);
      return {
        ...p,
        product_id: mapping?.product_id || null,
        unit_id: mapping?.unit_id || null,
      };
    });
    onApply({
      ...data,
      products: productsWithMapping,
    });
  }, [allMapped, sunatLines, getMapping, data, onApply, showToast]);

  useEffect(() => {
    if (!isOpen || !data) return;

    if (!supplierId) {
      setMappedIds({});
      setInitialMappings({});
      return undefined;
    }

    let cancelled = false;
    setLoadingMappings(true);
    getSunatMappings(supplierId)
      .then(async (res) => {
        if (cancelled) return;
        if (res.success && res.data) {
          const productIds = [
            ...new Set(
              Object.values(res.data)
                .map((mapping) => parseInt(mapping?.product_id, 10))
                .filter(Boolean),
            ),
          ];
          const ensureFn = onEnsureProductsRef.current;
          if (productIds.length > 0 && typeof ensureFn === 'function') {
            await ensureFn(productIds);
          }
          if (cancelled) return;

          // res.data viene por código proveedor; aplicar a cada línea del XML.
          const nextMapped = {};
          (data.products || []).forEach((prod, index) => {
            const lineKey = getSunatLineKey(prod, index);
            const code = getSunatSupplierCode(prod, lineKey);
            const saved = res.data[code] || res.data[lineKey];
            if (saved) {
              nextMapped[lineKey] = {
                product_id: saved.product_id ?? null,
                unit_id: saved.unit_id ?? null,
              };
            }
          });
          setMappedIds(nextMapped);
          setInitialMappings(nextMapped);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoadingMappings(false);
      });

    return () => {
      cancelled = true;
    };
    // No incluir onEnsureProducts: al elegir un producto cambia de identidad y
    // re-disparaba este efecto, borrando matches locales aún no guardados.
  }, [isOpen, data, supplierId]);

  if (!isOpen || !data) return null;

  const {
    document_type: docType,
    supplier_ruc: supplierRuc,
    supplier_name: supplierName,
    series,
    number,
    issue_date: issueDate,
    currency,
    base_imponible: baseImponible,
    igv,
    total,
    notes,
  } = data;

  const unmappedCount = sunatLines.filter((p) => {
    const m = mappedIds[p.lineKey];
    return !m || m.product_id == null || m.unit_id == null;
  }).length;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">
              Resultado SUNAT — {getSunatDocLabel(docType)}
            </h2>
            <button
              type="button"
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {!supplierId && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                Seleccione el proveedor en el formulario del documento para guardar y recuperar mapeos por código de
                producto del proveedor.
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Proveedor</h3>
                <div>
                  <p className="text-sm text-gray-500">RUC</p>
                  <p className="text-sm font-medium text-gray-900">{supplierRuc || '—'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Razón Social</p>
                  <p className="text-sm font-medium text-gray-900">{supplierName || '—'}</p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Documento</h3>
                <div>
                  <p className="text-sm text-gray-500">Tipo</p>
                  <p className="text-sm font-medium text-gray-900">{getSunatDocLabel(docType)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Serie - Número</p>
                  <p className="text-sm font-medium text-gray-900">
                    {series} - {number}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Fecha de Emisión</p>
                  <p className="text-sm font-medium text-gray-900">{issueDate || '—'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Moneda</p>
                  <p className="text-sm font-medium text-gray-900">{currency || '—'}</p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Montos</h3>
                <div className="flex justify-between">
                  <p className="text-sm text-gray-500">Base Imponible</p>
                  <p className="text-sm font-medium text-gray-900">
                    {baseImponible != null ? baseImponible.toFixed(2) : '—'}
                  </p>
                </div>
                <div className="flex justify-between">
                  <p className="text-sm text-gray-500">IGV</p>
                  <p className="text-sm font-medium text-gray-900">{igv != null ? igv.toFixed(2) : '—'}</p>
                </div>
                <div className="flex justify-between border-t border-gray-200 pt-2">
                  <p className="text-sm font-semibold text-gray-700">Total</p>
                  <p className="text-sm font-bold text-gray-900">{total != null ? total.toFixed(2) : '—'}</p>
                </div>
              </div>

              {notes && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    Observaciones
                  </h3>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{notes}</p>
                </div>
              )}
            </div>

            <div className="bg-gray-50 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-1">
                Productos — mapeo al catálogo interno
              </h3>
              <p className="text-xs text-gray-600 mb-3">
                En cada fila busque por <strong>SKU o nombre</strong> del producto interno. La clave de mapeo es el
                código del proveedor en el XML (si no viene, se usa la línea o la descripción).
                {productConversionsEnabled ? (
                  <>
                    {' '}
                    Si el producto tiene <strong>tasas personalizadas</strong>, el selector de unidad muestra primero
                    esas conversiones y luego el catálogo general.
                  </>
                ) : null}
              </p>
              {sunatLines.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[900px]">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 pr-2 text-gray-500 font-medium w-[110px]">Cód. proveedor</th>
                        <th className="text-left py-2 pr-2 text-gray-500 font-medium min-w-[220px]">
                          Producto interno (SKU o nombre)
                        </th>
                        <th className="text-left py-2 pr-2 text-gray-500 font-medium">Descripción SUNAT</th>
                        <th className="text-right py-2 pr-2 text-gray-500 font-medium">Cant.</th>
                        <th className="text-center py-2 pr-2 text-gray-500 font-medium min-w-[160px]">Unidad</th>
                        <th className="text-right py-2 pr-2 text-gray-500 font-medium">P. Unit.</th>
                        <th className="text-right py-2 text-gray-500 font-medium">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sunatLines.map((prod) => {
                        const mapping = getMapping(prod.lineKey);
                        const supplierCodeLabel = prod.internal_code?.trim()
                          ? prod.internal_code
                          : prod.lineKey;
                        return (
                          <tr key={prod.lineKey} className="border-b border-gray-100 last:border-0">
                            <td className="py-2 pr-2 text-xs text-gray-600 align-top pt-3 font-mono">
                              {supplierCodeLabel}
                            </td>
                            <td className="py-2 pr-2 align-top">
                              <ProductSelector
                                compact
                                value={mapping.product_id ? String(mapping.product_id) : ''}
                                onChange={(id) => handleProductChange(prod.lineKey, id, prod)}
                                onProductPicked={(product) => handleProductChange(prod.lineKey, product.id, prod, product)}
                                {...(productSelectorAsyncProps || { products })}
                              />
                            </td>
                            <td className="py-2 pr-2 text-gray-900 max-w-[200px] align-top pt-3">
                              <span className="line-clamp-2" title={prod.description}>
                                {prod.description || '—'}
                              </span>
                            </td>
                            <td className="py-2 pr-2 text-right text-gray-900 align-top pt-3">
                              <div>{prod.quantity != null ? prod.quantity.toFixed(2) : '—'}</div>
                              {mapping.product_id ? (
                                <QuantityConversionHint
                                  product={resolveProduct(mapping.product_id)}
                                  unitCode={
                                    mapping.unit_id
                                      ? units.find((u) => Number(u.id) === Number(mapping.unit_id))?.code
                                      : ''
                                  }
                                  quantity={prod.quantity}
                                />
                              ) : null}
                            </td>
                            <td className="py-2 px-2 align-top min-w-[180px]">
                              <UnitIdSelect
                                compact
                                units={units}
                                product={
                                  mapping.product_id
                                    ? resolveProduct(mapping.product_id)
                                    : null
                                }
                                value={mapping.unit_id || ''}
                                onChange={(id) => handleUnitChange(prod.lineKey, id)}
                                emptyLabel="— Sin unidad —"
                                disabled={!mapping.product_id}
                              />
                              {prod.unit_code ? (
                                <p className="text-[10px] text-gray-500 mt-0.5">SUNAT: {prod.unit_code}</p>
                              ) : null}
                            </td>
                            <td className="py-2 pr-2 text-right text-gray-900 align-top pt-3">
                              {prod.unit_price != null ? prod.unit_price.toFixed(2) : '—'}
                            </td>
                            <td className="py-2 text-right text-gray-900 font-medium align-top pt-3">
                              {prod.total != null ? prod.total.toFixed(2) : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-gray-400 italic">Sin líneas de producto</p>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 px-6 py-3 border-b border-gray-100 bg-gray-50">
            <p className="text-xs text-gray-500 w-full text-right">
              {loadingMappings
                ? 'Cargando mapeos guardados del proveedor…'
                : allMapped && !hasChanges
                  ? 'Todas las líneas tienen producto y unidad; mapeos guardados.'
                  : allMapped
                    ? hasChanges
                      ? 'Hay cambios sin guardar en los mapeos.'
                      : 'Listo para aplicar al formulario.'
                    : `${unmappedCount} línea(s) sin producto o unidad asignados.`}
            </p>
          </div>

          <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSaveMappings}
              disabled={saving || !hasChanges || !supplierId}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Guardando...' : 'Guardar mapeo'}
            </button>
            <button
              type="button"
              onClick={handleApply}
              disabled={!allMapped}
              className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Aplicar al formulario
            </button>
          </div>
        </div>
      </div>

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />

      {confirmClose && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">¿Cerrar sin mapear?</h3>
            <p className="text-sm text-gray-600 mb-6">
              Hay líneas sin producto o unidad asignados. Si cierras ahora, los datos no se aplicarán al formulario.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleCancelClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleConfirmClose}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SunatSearchResultModal;
